import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  List,
  ListItemText,
  Dialog,
  Button,
  ListItemButton,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { useQuery } from "@apollo/client";
import { GET_EVENTS_BY_DATE } from "../graphql/events";
import { Event } from "../types/event";
import EventFormDialog, { EventFormValues } from "./EventFormDialog";
import {
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

// Extended Event type to include trade information
interface EventWithTrade extends Event {
  trade?: {
    id: number;
    symbol: string;
    side: string;
  };
}

function normalizeToLocalMidnight(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isSameDay(date1: Date, date2: Date) {
  const d1 = normalizeToLocalMidnight(date1);
  const d2 = normalizeToLocalMidnight(date2);
  return d1.getTime() === d2.getTime();
}

const getEventTypeColor = (eventType?: string) => {
  switch (eventType) {
    case "ENTRY_SIGNAL":
      return "success";
    case "EXIT_SIGNAL":
      return "error";
    case "EARNINGS_REPORT":
      return "warning";
    case "NEWS_EVENT":
      return "info";
    default:
      return "default";
  }
};

const getImpactIcon = (impact?: string) => {
  switch (impact) {
    case "POSITIVE":
      return <TrendingUpIcon color="success" />;
    case "NEGATIVE":
      return <TrendingDownIcon color="error" />;
    case "NEUTRAL":
      return <RemoveIcon color="action" />;
    default:
      return <WarningIcon color="warning" />;
  }
};

export default function EventsCalendar() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [editingEvent, setEditingEvent] = useState<EventWithTrade | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_EVENTS_BY_DATE, {
    variables: {
      date: selectedDate
        ? selectedDate.toISOString()
        : new Date().toISOString(),
    },
    skip: !selectedDate,
  });

  const events: EventWithTrade[] = data?.eventsByDate || [];

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleEventClick = (event: EventWithTrade) => {
    setEditingEvent(event);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingEvent(null);
    refetch();
  };

  const formatEventTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 4,
        p: 2,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Events Calendar
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          width: "100%",
          maxWidth: 1200,
        }}
      >
        {/* Calendar Section */}
        <Box sx={{ flex: { xs: "1", md: "1 1 50%" } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Date
              </Typography>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateChange}
                sx={{ width: "100%" }}
              />
            </CardContent>
          </Card>
        </Box>

        {/* Events Section */}
        <Box sx={{ flex: { xs: "1", md: "1 1 50%" } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Events for {selectedDate?.toLocaleDateString() || ""}
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">
                  Error loading events: {error.message}
                </Alert>
              ) : events.length === 0 ? (
                <Box sx={{ textAlign: "center", p: 3 }}>
                  <EventIcon
                    sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    No events scheduled for this date.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {events.map((event) => (
                    <ListItemButton
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      sx={{
                        mb: 1,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <Chip
                            label={event.eventType || event.type}
                            color={getEventTypeColor(event.eventType) as any}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          {event.impact && (
                            <Tooltip title={`Impact: ${event.impact}`}>
                              <IconButton size="small">
                                {getImpactIcon(event.impact)}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Typography variant="caption" sx={{ ml: "auto" }}>
                            {formatEventTime(event.date)}
                          </Typography>
                        </Box>

                        <ListItemText
                          primary={
                            <Typography variant="subtitle2">
                              {event.eventType || event.type}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              {event.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {event.description}
                                </Typography>
                              )}
                              {event.probability && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Probability: {event.probability}%
                                </Typography>
                              )}
                              {event.targetPrice && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  Target: ${event.targetPrice}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Event Form Dialog */}
      <EventFormDialog
        open={dialogOpen}
        initialValues={
          editingEvent
            ? {
                ...editingEvent,
                date: editingEvent.date
                  ? new Date(editingEvent.date)
                  : new Date(),
              }
            : undefined
        }
        onClose={handleDialogClose}
        onSave={handleDialogClose}
      />
    </Box>
  );
}
